import { makeStyles } from '@mui/styles';
import { neutral3 } from 'apollo-react/colors';
import Panel from 'apollo-react/components/Panel';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactResizeDetector from 'react-resize-detector';

import { clearErrors } from '../../actions/ErrorActions';
import { clearWarnings } from '../../actions/WarningActions';
import CustomLoader from '../../components/CustomLoader';
import EditorErrorPage from '../../components/EditorErrorPage';
import Layout from '../../components/Layout';
import PanelWrapper from '../../components/PanelWrapper';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt';
import { SMART_INSIGHT } from '../../constants/Urls';
import { hasFetchError } from '../../form-designer/selectors/common.selectors';
import {
  clearReportDesignerStore,
  loadReport,
} from '../../report-designer/actions/report-designer.actions';
import ControlProperties from '../../report-designer/components/ControlProperties';
import EditorHeader from '../../report-designer/components/EditorHeader';
import ReportManager from '../../report-designer/ReportManager';
import {
  getSelectedTabIndex,
  hasReportModelUnsavedChanges,
  isReportLoading,
} from '../../report-designer/selectors/report-designer.selectors';

const useStyles = makeStyles({
  container: {
    position: 'relative',
  },
  wrapperStyled: {
    display: 'flex',
    height: 'calc(100vh - 66px)',
    overflow: 'hidden',
  },
  reportWrapperStyled: {
    height: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
    width: '100%',
  },
  reportEditorContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 100%',
    minWidth: 0,
    height: '97vh',
    overflow: 'hidden',
  },
  panelGroup: {
    display: 'flex',
    padding: 0,
    overflow: 'hidden',
  },
  loader: {
    height: '100vh',
    overflow: 'hidden',
  },
  leftPanel: {
    padding: '0 5px',
  },
  rightPanel: {
    padding: '20px 20px 0 20px',
    height: 'calc(100vh - 157px)',
    overflow: 'auto',
  },
  reportManagerContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    overflow: 'auto',
  },
  handle: {
    position: 'absolute',
    right: 0,
    height: 1,
    width: '100%',
    backgroundColor: neutral3,
  },
});

const ReportEditor = ({ location }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const isLoading = useSelector(isReportLoading);
  const selectedTabIndex = useSelector(getSelectedTabIndex);
  const isFetchError = useSelector(hasFetchError);
  const hasUnsavedChanges = useSelector(hasReportModelUnsavedChanges);

  const [headerHeight, setHeaderHeight] = useState(0);

  const { reportId, isImported } = qs.parse(location.search);

  useEffect(() => {
    if (!isImported) {
      dispatch(loadReport(reportId));
    }
  }, [dispatch, reportId, isImported]);

  useEffect(() => {
    return () => {
      dispatch(clearErrors());
      dispatch(clearWarnings());
      dispatch(clearReportDesignerStore());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldHideProperties = selectedTabIndex !== 0;

  return (
    <Layout dashboardLink={SMART_INSIGHT}>
      <div className={classes.wrapperStyled}>
        <div className={classes.reportWrapperStyled} ui-ta="ScrollingElement">
          {isFetchError ? (
            <EditorErrorPage />
          ) : isLoading ? (
            <div className={classes.loader}>
              <CustomLoader />
            </div>
          ) : (
            <>
              <div className={classes.reportEditorContainer}>
                <div>
                  <EditorHeader />
                  <ReactResizeDetector handleHeight onResize={(w, h) => setHeaderHeight(h)} />
                </div>
                <PanelWrapper classes={classes}>
                  <Panel
                    width={`${shouldHideProperties ? 100 : 70}%`}
                    className={classes.leftPanel}
                    resizable={!shouldHideProperties}
                    hideButton
                  >
                    <div className={classes.reportManagerContainer}>
                      <ReportManager headerHeight={headerHeight} />
                    </div>
                  </Panel>
                  {!shouldHideProperties && (
                    <Panel width="100%" className={classes.rightPanel} hideButton>
                      <ControlProperties />
                    </Panel>
                  )}
                </PanelWrapper>
              </div>
              <UnsavedChangesPrompt hasUnsavedChanges={hasUnsavedChanges} />
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ReportEditor;
